import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Popconfirm, Progress, Radio, Spin } from 'antd';
import type { ButtonProps } from 'antd/es/button';
import { CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { isPlasticNeedingColor } from '@partsbadger/utils/lib/common/materialColor';
import { Coatings, Finishes, Materials } from './DropDowns';
import QuantitySelector from './QuoteLineItem/QuantitySelector';
import QuoteStore from '../stores/QuoteStore';
import {
    BulkPart,
    BulkResult,
    BulkSpecs,
    describeFailedCheck,
    describePreview,
    describeSummary,
    failedParts,
    hasSpecs,
    runBulkSpecs,
    summarizeResults,
} from '../utils/bulkSpecs';
import {
    cleanQuantities,
    describeQuantityPreview,
    describeQuantitySummary,
    failedQuantityParts,
    linesPerRequestForQuantities,
    QuantityMode,
    QuantityResult,
    runBulkQuantities,
    summarizeQuantityResults,
} from '../utils/bulkQuantities';

interface Props {
    /** Selected parts, each with every variant (line item) it has. */
    parts: BulkPart[];
    for_staff?: boolean;
    onSelectAll: () => void;
    onSelectMissingSpecs: () => void;
    onClear: () => void;
}

type Option = { key: string; label: string } | null | undefined;
type Phase = 'idle' | 'running' | 'done';

const PREVIEW_DELAY_MS = 400;
const PREVIEW_LINES_PER_REQUEST = 25;

const CUSTOM_NOTICE = 'Custom values and colored plastics need details per part. Set them on each part.';

/**
 * Change the material, finish and/or coating of the selected parts at once. Only the fields
 * chosen change; the preview says what will change before it is applied, and the result
 * shows how each part ended (priced, manual quote, waiting for analysis, failed).
 */
const BulkSpecsPanel = ({ parts, for_staff = false, onSelectAll, onSelectMissingSpecs, onClear }: Props) => {
    const [material, setMaterial] = useState<Option>(null);
    const [finish, setFinish] = useState<Option>(null);
    const [coating, setCoating] = useState<Option>(null);
    const [quantities, setQuantities] = useState<number[]>([]);
    const [mode, setMode] = useState<QuantityMode>('add');
    const [notice, setNotice] = useState<string | null>(null);

    const [preview, setPreview] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewChanges, setPreviewChanges] = useState(0);
    const [quantityPreview, setQuantityPreview] = useState<string | null>(null);
    const [quantityChanges, setQuantityChanges] = useState(0);
    const [linesToRemove, setLinesToRemove] = useState(0);

    const [phase, setPhase] = useState<Phase>('idle');
    const [progress, setProgress] = useState({ done: 0, total: 0, label: 'Updating' });
    const [results, setResults] = useState<BulkResult[]>([]);
    const [quantityResults, setQuantityResults] = useState<QuantityResult[]>([]);
    const appliedParts = useRef<BulkPart[]>([]);
    const previewToken = useRef(0);

    const specs: BulkSpecs = useMemo(
        () => ({
            ...(material ? { material: Number(material.key) } : {}),
            ...(finish ? { finishes: [Number(finish.key)] } : {}),
            ...(coating ? { coatings: [Number(coating.key)] } : {}),
        }),
        [material, finish, coating]
    );

    const selectionKey = parts.map(part => `${part.key}:${part.lineItemIds.join(',')}`).join('|');
    const running = phase === 'running';

    const hasQuantities = quantities.length > 0;

    // The preview: a dry run for the current selection, specs and quantities.
    useEffect(() => {
        const token = (previewToken.current += 1);
        if ((!hasSpecs(specs) && !hasQuantities) || parts.length === 0 || phase !== 'idle') {
            setPreview(null);
            setPreviewChanges(0);
            setQuantityPreview(null);
            setQuantityChanges(0);
            setLinesToRemove(0);
            setPreviewLoading(false);
            return undefined;
        }
        setPreviewLoading(true);
        const timer = setTimeout(async () => {
            try {
                const [specsDryRun, quantitiesDryRun] = await Promise.all([
                    hasSpecs(specs)
                        ? runBulkSpecs({
                              parts,
                              send: ids => QuoteStore.updateMultipleSpecs(ids, specs, true),
                              partsPerRequest: PREVIEW_LINES_PER_REQUEST,
                          })
                        : Promise.resolve([] as BulkResult[]),
                    hasQuantities
                        ? runBulkQuantities({
                              parts,
                              send: ids => QuoteStore.updateMultipleQuantities(ids, quantities, mode, true),
                              partsPerRequest: PREVIEW_LINES_PER_REQUEST,
                              maxLines: linesPerRequestForQuantities(quantities.length),
                          })
                        : Promise.resolve([] as QuantityResult[]),
                ]);
                if (token !== previewToken.current) return;
                const summary = summarizeResults(specsDryRun);
                setPreviewChanges(summary.updated);
                setPreview(
                    hasSpecs(specs)
                        ? summary.failed
                            ? describeFailedCheck(specsDryRun)
                            : describePreview(summary) || 'Nothing to change'
                        : null
                );
                const quantitySummary = summarizeQuantityResults(quantitiesDryRun);
                setQuantityChanges(quantitySummary.updated);
                setLinesToRemove(quantitySummary.linesRemoved);
                setQuantityPreview(
                    hasQuantities
                        ? quantitySummary.failed
                            ? describeFailedCheck(quantitiesDryRun)
                            : describeQuantityPreview(quantitySummary) || 'Nothing to change'
                        : null
                );
            } finally {
                if (token === previewToken.current) setPreviewLoading(false);
            }
        }, PREVIEW_DELAY_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specs, quantities, mode, selectionKey, phase]);

    const choose = (setter: (value: Option) => void, kind: 'material' | 'finish' | 'coating') => (value: Option) => {
        if (value && value.label === 'Custom') {
            setNotice(CUSTOM_NOTICE);
            return;
        }
        if (kind === 'material' && value) {
            const chosen = QuoteStore.materials?.find(item => String(item.id) === String(value.key));
            if (isPlasticNeedingColor(chosen)) {
                setNotice(CUSTOM_NOTICE);
                return;
            }
        }
        setNotice(null);
        setter(value);
    };

    const run = async (
        specParts: BulkPart[],
        quantityParts: BulkPart[],
        previousSpecs: BulkResult[],
        previousQuantities: QuantityResult[]
    ) => {
        appliedParts.current = specParts;
        setPhase('running');
        const lineTotal = (list: BulkPart[]) => list.reduce((sum, part) => sum + part.lineItemIds.length, 0);

        if (hasSpecs(specs) && specParts.length > 0) {
            setProgress({ done: 0, total: lineTotal(specParts), label: 'Updating specifications' });
            const fresh = await runBulkSpecs({
                parts: specParts,
                send: ids => QuoteStore.updateMultipleSpecs(ids, specs),
                onProgress: (soFar, all) =>
                    setProgress({ done: soFar.length, total: all, label: 'Updating specifications' }),
            });
            // A retry replaces the earlier result of the same lines.
            const retried = new Set(fresh.map(r => r.id));
            setResults([...previousSpecs.filter(r => !retried.has(r.id)), ...fresh]);
        }

        if (hasQuantities && quantityParts.length > 0) {
            setProgress({ done: 0, total: lineTotal(quantityParts), label: 'Updating quantities' });
            const fresh = await runBulkQuantities({
                parts: quantityParts,
                send: ids => QuoteStore.updateMultipleQuantities(ids, quantities, mode),
                maxLines: linesPerRequestForQuantities(quantities.length),
                onProgress: (soFar, all) =>
                    setProgress({
                        done: soFar.reduce((sum, r) => sum + (r.line_item_ids?.length ?? 1), 0),
                        total: all,
                        label: 'Updating quantities',
                    }),
            });
            const retriedLines = new Set(fresh.flatMap(r => r.line_item_ids ?? [r.id]));
            setQuantityResults([
                ...previousQuantities.filter(r => !(r.line_item_ids ?? [r.id]).some(id => retriedLines.has(id))),
                ...fresh,
            ]);
        }

        setPhase('done');
    };

    const apply = () => run(parts, parts, [], []);
    const retryFailed = () =>
        run(failedParts(parts, results), failedQuantityParts(parts, quantityResults), results, quantityResults);

    const dismiss = () => {
        setPhase('idle');
        setResults([]);
        setQuantityResults([]);
        setMaterial(null);
        setFinish(null);
        setCoating(null);
        setQuantities([]);
        setMode('add');
        setNotice(null);
    };

    const summary = summarizeResults(results);
    const quantitySummary = summarizeQuantityResults(quantityResults);
    const failedCount = summary.failed + quantitySummary.failed;
    const lineCount = parts.reduce((sum, part) => sum + part.lineItemIds.length, 0);
    const partsLabel = `${parts.length} ${parts.length === 1 ? 'part' : 'parts'}`;
    const canApply =
        (hasSpecs(specs) || hasQuantities) &&
        parts.length > 0 &&
        !running &&
        !previewLoading &&
        previewChanges + quantityChanges > 0;
    // Replacing quantities deletes the lines of the quantities that are dropped.
    const needsConfirm = mode === 'replace' && hasQuantities && linesToRemove > 0;

    return (
        <section
            aria-label="Change specifications"
            className="bulk-specs-panel"
            data-testid="bulk-specs-panel"
            data-phase={phase === 'idle' && previewLoading ? 'previewing' : phase}
        >
            <div className="bulk-specs-panel-header">
                <div className="bulk-specs-panel-title">
                    <strong data-testid="bulk-specs-count">{partsLabel} selected</strong>
                    <div className="bulk-specs-panel-selection-options" role="group" aria-label="Selection options">
                        <span className="bulk-specs-panel-selection-options-label">Selection options:</span>
                        <Button
                            size="small"
                            onClick={onSelectAll}
                            disabled={running}
                            data-testid="bulk-specs-select-all"
                        >
                            Select all
                        </Button>
                        <Button
                            size="small"
                            onClick={onSelectMissingSpecs}
                            disabled={running}
                            data-testid="bulk-specs-select-missing"
                        >
                            Select parts missing specs
                        </Button>
                    </div>
                </div>
                <Button
                    type="text"
                    aria-label="Clear selection"
                    icon={<CloseOutlined />}
                    onClick={onClear}
                    disabled={running}
                    data-testid="bulk-specs-clear"
                />
            </div>

            <div className="bulk-specs-panel-subtitle">
                Change specifications and quantities for all the variants of these parts. Leave a field empty to keep
                what each part has.
            </div>

            <div className="bulk-specs-panel-fields">
                <label className="bulk-specs-panel-field">
                    <span>Material</span>
                    <Materials
                        materials={QuoteStore.materials}
                        for_staff={for_staff}
                        value={material as any}
                        onChange={choose(setMaterial, 'material') as any}
                        data-testid="bulk-specs-material"
                        allowClear
                        disabled={running}
                        placeholder="Keep current"
                    />
                </label>
                <label className="bulk-specs-panel-field">
                    <span>Finish</span>
                    <Finishes
                        finishes={QuoteStore.finishes}
                        for_staff={for_staff}
                        value={finish as any}
                        onChange={choose(setFinish, 'finish') as any}
                        data-testid="bulk-specs-finish"
                        allowClear
                        disabled={running}
                        placeholder="Keep current"
                    />
                </label>
                <label className="bulk-specs-panel-field">
                    <span>Coating</span>
                    <Coatings
                        coatings={QuoteStore.coatings}
                        for_staff={for_staff}
                        value={coating as any}
                        onChange={choose(setCoating, 'coating') as any}
                        data-testid="bulk-specs-coating"
                        allowClear
                        disabled={running}
                        placeholder="Keep current"
                    />
                </label>
            </div>

            <div className="bulk-specs-panel-quantities" data-testid="bulk-specs-quantities">
                <div className="bulk-specs-panel-quantities-head">
                    <span className="bulk-specs-panel-label">Quantities</span>
                    <Radio.Group
                        size="small"
                        optionType="button"
                        buttonStyle="solid"
                        value={mode}
                        disabled={running}
                        data-testid="bulk-quantities-mode"
                        onChange={event => setMode(event.target.value)}
                        options={[
                            { label: 'Add', value: 'add' },
                            { label: 'Replace', value: 'replace' },
                        ]}
                    />
                    {hasQuantities ? (
                        <Button
                            type="link"
                            size="small"
                            disabled={running}
                            onClick={() => setQuantities([])}
                            data-testid="bulk-quantities-clear"
                        >
                            Clear quantities
                        </Button>
                    ) : null}
                </div>
                <QuantitySelector
                    quantities={quantities}
                    disabled={running}
                    onChange={next => setQuantities(cleanQuantities(next))}
                />
                <div className="bulk-specs-panel-hint">
                    {mode === 'add'
                        ? 'Existing quantities stay as they are. Leave empty to keep them.'
                        : 'Each part ends with exactly these quantities; the others are removed.'}
                </div>
            </div>

            {notice ? <Alert type="warning" showIcon message={notice} className="bulk-specs-panel-alert" /> : null}

            {phase === 'idle' && (hasSpecs(specs) || hasQuantities) ? (
                <div className="bulk-specs-panel-preview" data-testid="bulk-specs-preview" aria-live="polite">
                    {previewLoading ? <Spin size="small" /> : null}
                    {previewLoading ? (
                        <span>Checking…</span>
                    ) : (
                        <div>
                            {preview ? <div data-testid="bulk-specs-preview-specs">{preview}</div> : null}
                            {quantityPreview ? (
                                <div data-testid="bulk-specs-preview-quantities">Quantities: {quantityPreview}</div>
                            ) : null}
                        </div>
                    )}
                </div>
            ) : null}

            {running ? (
                <div className="bulk-specs-panel-progress" data-testid="bulk-specs-progress">
                    <Progress
                        percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
                        size="small"
                        status="active"
                    />
                    <span>
                        {progress.label} {progress.done} of {progress.total}… prices are recalculated for each part.
                    </span>
                </div>
            ) : null}

            {phase === 'done' ? (
                <Alert
                    className="bulk-specs-panel-alert"
                    data-testid="bulk-specs-result"
                    type={failedCount ? 'warning' : 'success'}
                    showIcon
                    message={failedCount ? 'Some parts could not be updated' : 'Parts updated'}
                    description={
                        <div>
                            {results.length ? <div>{describeSummary(summary)}</div> : null}
                            {quantityResults.length ? (
                                <div data-testid="bulk-quantities-result">
                                    Quantities: {describeQuantitySummary(quantitySummary)}
                                </div>
                            ) : null}
                            {failedCount ? (
                                <ul className="bulk-specs-panel-failures" data-testid="bulk-specs-failures">
                                    {[...results, ...quantityResults]
                                        .filter(r => r.status === 'failed')
                                        .slice(0, 5)
                                        .map(r => (
                                            <li key={r.id} data-testid="bulk-specs-failure" data-line-item-id={r.id}>
                                                Line {r.id}: {r.reason}
                                            </li>
                                        ))}
                                </ul>
                            ) : null}
                        </div>
                    }
                    action={
                        <div className="bulk-specs-panel-result-actions">
                            {failedCount ? (
                                <Button size="small" onClick={retryFailed} data-testid="bulk-specs-retry">
                                    Retry failed
                                </Button>
                            ) : null}
                            <Button size="small" onClick={dismiss} data-testid="bulk-specs-dismiss">
                                Done
                            </Button>
                        </div>
                    }
                />
            ) : null}

            {phase !== 'done' ? (
                <div className="bulk-specs-panel-actions">
                    <Button onClick={onClear} disabled={running} data-testid="bulk-specs-cancel">
                        Cancel
                    </Button>
                    <Popconfirm
                        disabled={!needsConfirm || !canApply}
                        placement="topRight"
                        title={`Remove ${linesToRemove} ${
                            linesToRemove === 1 ? 'line' : 'lines'
                        }? The quantities that are not in the list are deleted with their selected prices.`}
                        okText="Replace quantities"
                        okButtonProps={{ 'data-testid': 'bulk-quantities-replace-confirm' } as ButtonProps}
                        onConfirm={apply}
                    >
                        <Button
                            type="primary"
                            onClick={needsConfirm ? undefined : apply}
                            disabled={!canApply}
                            loading={running}
                            data-testid="bulk-specs-apply"
                        >
                            {previewChanges + quantityChanges > 0 && lineCount > 0 ? `Apply to ${partsLabel}` : 'Apply'}
                        </Button>
                    </Popconfirm>
                </div>
            ) : null}
        </section>
    );
};

export default observer(BulkSpecsPanel);
